import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../lib/AppContext';
import { readableTextOn } from '../lib/styles';
import {
  addScoutAccount,
  backfillLegacyLocalStorage,
  SCOUT_STAGES as STAGES,
  SCOUT_SOURCES,
} from '../lib/scoutQuickEntry';
import { THEME_2026 } from '../lib/theme2026';
import Button from './Button';
import PhonePlusMenu from './PhonePlusMenu';
import LogSightingDialog from './LogSightingDialog';
import { useIsMobile } from '../lib/useIsMobile';

/**
 * QuickScoutFloater — global floating launcher for Targets quick-entry.
 * v1.7.39.188 — renamed user-facing copy from "Scout"/"Scout Tracker" to
 * "Targets" (Shane, 2026-08-03) -- Scout is reserved exclusively for the
 * real LinkedIn capture tool going forward. Still writes to scout_accounts
 * (real table name unchanged, lower-risk scoped fix). Component/file name
 * (QuickScoutFloater) and internal function names (addScoutAccount) left
 * as-is for now -- same scoped-fix approach as the nav rename.
 * v1.6.87 · B23 Phase 2 — UI shell
 * v1.6.88 · B17 — wired to Supabase (was localStorage)
 *
 * - FAB button in bottom-right corner (above mobile nav)
 * - Global keyboard shortcut: Cmd+K / Ctrl+K
 * - Writes directly to scout_accounts (source='field') so entries appear
 *   cross-device and cross-user immediately
 * - Auto-tags with activePartner (partner_id)
 * - GOV palette: slate/crimson, no beige
 */

export default function QuickScoutFloater({ onNavigate }) {
  const { activePartner, showToast } = useAppContext();
  const isMobile = useIsMobile();
  // v1.7.24.e — the floating button follows the active persona's brand color (was hardcoded
  // Well Repd crimson). Text auto-contrasts so it's readable on light brands (e.g. FoxSet).
  const accent = activePartner?.brand_color || THEME_2026.brassAccent;
  const accentInk = readableTextOn(accent);
  const [open, setOpen] = useState(false);
  const [sightingOpen, setSightingOpen] = useState(false); // Log a Sighting dialog (desktop)
  const [hubOpen, setHubOpen] = useState(false); // v1.7.24.f — quick-add hub menu
  const [accountName, setAccountName] = useState('');
  const [stage, setStage] = useState('target');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const partnerName = activePartner?.name || null;
  const partnerId = activePartner?.id || null;

  // Silent backfill of any pre-B17 localStorage entries (runs once per browser)
  useEffect(() => { backfillLegacyLocalStorage(); }, []);

  // Global Cmd/Ctrl+K shortcut
  useEffect(() => {
    const handler = (e) => {
      const isK = e.key === 'k' || e.key === 'K';
      if (isK && (e.metaKey || e.ctrlKey)) {
        // Don't hijack browser search or interfere with inputs unless closing
        e.preventDefault();
        setOpen((p) => !p);
      }
      if (e.key === 'Escape') {
        if (open) setOpen(false);
        if (hubOpen) setHubOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, hubOpen]);

  // v1.7.24.f — quick-add hub actions. Lead opens the inline Scout modal (no navigation);
  // the others jump to the right module with a flag to auto-open its add form.
  const hubGo = useCallback((action) => {
    setHubOpen(false);
    if (action === 'lead') { setOpen(true); return; }
    if (action === 'sighting') { setSightingOpen(true); return; }
    if (!onNavigate) return;
    if (action === 'contact') onNavigate('contacts', { quickAdd: true });
    else if (action === 'task') onNavigate('tasks', { quickAdd: true });
    else if (action === 'quote') onNavigate('quotes', { quickAdd: true });
  }, [onNavigate]);

  const handleAdd = useCallback(async () => {
    const name = accountName.trim();
    if (!name) { showToast?.('Enter an account name.', 'error'); return; }
    if (saving) return;
    setSaving(true);
    const { error } = await addScoutAccount({
      name,
      stage,
      notes,
      source: SCOUT_SOURCES.FIELD,
      partnerId,
      ownerLabel: 'Shane',
    });
    setSaving(false);
    if (error) { showToast?.(error.message || 'Failed to save.', 'error'); return; }
    setAccountName('');
    setNotes('');
    setStage('target');
    setOpen(false);
    showToast?.(`Added "${name}" to Targets${partnerName ? ` · ${partnerName}` : ''}.`, 'success');
  }, [accountName, stage, notes, partnerName, partnerId, showToast, saving]);

  const canAdd = accountName.trim().length > 0;

  // On a phone the + button is its own short menu (PhonePlusMenu); the target quick-entry below is the desktop's.
  if (isMobile) return <PhonePlusMenu onNavigate={onNavigate} />;

  return (
    <>
      {/* v1.7.24.f — Quick-add hub menu (Lead / Contact / Task / Quote) */}
      {hubOpen && (
        <>
          <div onClick={() => setHubOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 479 }} />
          <div style={{ position: 'fixed', right: 20, bottom: 148, zIndex: 481, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            {[
              ['lead', 'Lead', '◎'],
              ['contact', 'Contact', '☷'],
              ['task', 'Task', '✓'],
              ['quote', 'Quote', '$'],
              ['sighting', 'Log a Sighting', '◉'],
            ].map(([action, label, glyph]) => (
              <button
                key={action}
                onClick={() => hubGo(action)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: '#ffffff', border: '1px solid var(--gov-slate-200)', borderRadius: 24,
                  padding: '8px 14px 8px 10px', cursor: 'pointer',
                  boxShadow: '0 3px 12px rgba(0,0,0,0.15)', fontSize: 13, fontWeight: 700, color: 'var(--gov-text-primary)',
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{ width: 26, height: 26, borderRadius: '50%', background: accent, color: accentInk, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>{glyph}</span>
                {label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* FAB launcher — bottom-right, above mobile nav. Tap opens the quick-add hub. */}
      <button
        onClick={() => setHubOpen((p) => !p)}
        title="Quick Add (Cmd+K for Lead)"
        aria-label="Open Quick Add"
        style={{
          position: 'fixed',
          right: 20,
          bottom: 84,
          width: 52,
          height: 52,
          borderRadius: '50%',
          background: accent,
          color: accentInk,
          border: '2px solid #ffffff',
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
          cursor: 'pointer',
          zIndex: 480,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          fontWeight: 700,
          padding: 0,
          transform: hubOpen ? 'rotate(45deg)' : 'rotate(0deg)',
          transition: 'transform 0.18s ease',
        }}
      >
        +
      </button>

      <LogSightingDialog open={sightingOpen} onClose={() => setSightingOpen(false)} />

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.55)',
            zIndex: 950,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: 'min(15vh, 120px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              border: '1px solid var(--gov-text-primary)',
              borderRadius: 6,
              width: 'min(560px, 92vw)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{ background: 'var(--gov-text-primary)', color: '#ffffff', padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gov-slate-400)' }}>
                  Quick Target {partnerName ? `· ${partnerName}` : ''}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>Drop a prospect into Targets</div>
              </div>
              <Button
                size="sm"
                onClick={() => setOpen(false)}
                aria-label="Close"
                style={{ background: 'none', border: '1px solid var(--gov-slate-600)', color: 'var(--gov-slate-200)', fontSize: 12 }}
              >
                Esc
              </Button>
            </div>

            {/* Body */}
            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                autoFocus
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && canAdd) handleAdd(); }}
                placeholder="Account name (e.g. EOG Resources)"
                style={{ width: '100%', background: '#ffffff', border: '1px solid var(--gov-slate-400)', borderRadius: 4, padding: '10px 12px', fontSize: 14, color: 'var(--gov-text-primary)', outline: 'none', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  value={stage}
                  onChange={(e) => setStage(e.target.value)}
                  style={{ flex: 1, background: '#ffffff', border: '1px solid var(--gov-slate-400)', borderRadius: 4, padding: '10px 12px', fontSize: 13, color: 'var(--gov-text-primary)', fontWeight: 600, cursor: 'pointer', boxSizing: 'border-box' }}
                >
                  {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && canAdd) handleAdd(); }}
                  placeholder="Quick note (optional)"
                  style={{ flex: 2, background: '#ffffff', border: '1px solid var(--gov-slate-400)', borderRadius: 4, padding: '10px 12px', fontSize: 13, color: 'var(--gov-text-primary)', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                <div style={{ fontSize: 11, color: 'var(--gov-slate-500)' }}>
                  Press <kbd style={{ background: 'var(--gov-slate-100)', border: '1px solid var(--gov-slate-200)', borderRadius: 3, padding: '1px 5px', fontSize: 10 }}>⌘K</kbd> anytime to open · <kbd style={{ background: 'var(--gov-slate-100)', border: '1px solid var(--gov-slate-200)', borderRadius: 3, padding: '1px 5px', fontSize: 10 }}>Enter</kbd> to save
                </div>
                <Button
                  variant="primary"
                  color={accent}
                  onClick={handleAdd}
                  disabled={!canAdd}
                  loading={saving}
                  loadingText="Saving…"
                  style={{ color: accentInk, letterSpacing: '0.1em', textTransform: 'uppercase' }}
                >
                  Add to Targets
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
